import React from 'react';
import { FormattedMessage } from 'react-intl';
import HelloModal from 'common/components/hello-modal';
import { Template } from 'hellospa/page/prep-and-send/data/types';
import uuid from 'uuid/v4';
import { useSelector } from 'react-redux';
import Pagination from 'common/components/pagination';
import HfReactHelper from 'common/utils/hf-react-helper';
import { appContext } from 'hellospa/app-context';
import { getTemplates, isBulkSend, canCreateEditTemplate } from 'hellospa/page/prep-and-send/data/selectors';
import { TemplateResponse } from 'hellospa/app-context/actions/prep-and-send';
import Item from './item';
import styles from './styles.scss';
import Button, { ButtonType } from 'hellospa/foundation/control/button';
import { trackHeapCustomEvent } from 'common/utils/heap';
import { useFeatureFlag } from 'hellospa/components/feature-flags';


type Response = {
  total: number,
  pageSize: number,
  templates: Template[]
}

type State = {
  requestError: boolean,
  requestGuid: string | null,
  pageNum: number,
  data: Response | null,
  totalResults: number,
  pageSize: number,
}

/**
 * Returns if the template should be hidden or not by checking if it's
 * locked or is part of the templates in the current request
 * @param  {Template} template
 * @param  {TemplateResponse} templates in current request
 * @return {boolean}
 */
function notHidden(template: Template, templates: TemplateResponse[]): boolean {
  return !template.isLocked && !templates.map((t) => t.templateGuid).includes(template.id);
}

const defaultState: State = {
  requestError: false,
  requestGuid: null,
  pageNum: 1,
  data: null,
  totalResults: 0,
  pageSize: 100,
};
function useTemplateList(isOpen: boolean, filter?: string) {
  const { fetchTemplateList } = React.useContext(appContext);
  const [state, setState] = React.useState<State>(defaultState);

  const fetchTemplates = React.useCallback(async (pageNum: number): Promise<void> => {
    const requestGuid = uuid();
    setState({
      ...state,
      requestGuid,
      requestError: false,
      pageNum,
      data: null,
    });

    let data: Response;
    try {
      data = await fetchTemplateList({
        pageNum,
        filter: filter || 'all',
      });

      trackHeapCustomEvent('Add Templates', { 'Template Count': data.total });

      setState((state: State) => {
        if (state.requestGuid === requestGuid) {
          return {
            ...state,
            totalResults: data.total,
            data,
          };
        }
        return state;
      });

    } catch (e) {
      data = { total: state.totalResults, pageSize: state.pageSize, templates: [] };
      setState({ ...state, requestError: true });
    }

  }, [fetchTemplateList, state, filter]);

  const fetchedInitialPage = React.useRef(false);
  React.useEffect(() => {
    if (isOpen) {
      // mimicking componentDidMount
      if (!fetchedInitialPage.current) {
        fetchTemplates(state.pageNum);
      }
      return () => {
        fetchedInitialPage.current = true;
      };
    }
    // Reset to the default state when the modal closes
    setState(defaultState);
    fetchedInitialPage.current = false;
    return () => { };
  }, [fetchTemplates, state.pageNum, fetchedInitialPage, isOpen]);


  const totalResults = state.data ? state.data.total : state.totalResults;
  const lastPage = 1 + Math.floor(totalResults / state.pageSize);
  const templates = state.data != null ? state.data.templates : [];
  const hasError = state.requestError;

  const foo = {
    totalResults,
    lastPage,
    templates,
    hasError,
    isFetching: state.data == null,
    pageNum: state.pageNum,
  };

  const tuple: [typeof foo, typeof fetchTemplates] = [foo, fetchTemplates];
  return tuple;
}

type Props = {
  isOpen: boolean,
  togglePicker: (status: boolean) => () => void,
  onSelectTemplate: (template: Template) => void,
  filter?: string,
};

export default function TemplatePicker({ isOpen, togglePicker, onSelectTemplate, filter }: Props) {

  const templatesInRequest = useSelector(getTemplates);
  const isTemplateRedesignExperience = useFeatureFlag('HelloSign_Stars_20210727_Prep_Send_Template_Modal_Redesign');
  const showModalFooter = useSelector(canCreateEditTemplate) && isTemplateRedesignExperience;
  const bulkSend = useSelector(isBulkSend);
  const [{
    isFetching,
    totalResults, lastPage, templates, hasError, pageNum,
  }, fetchTemplates] = useTemplateList(isOpen, filter);
  const handleCreateTemplate = () => {
    trackHeapCustomEvent('Click_Create_Template_P&S_Modal', { button_type: 'v1' });
  };

  let content: JSX.Element | JSX.Element[] = <div />;
  let contentLabel = (
    <FormattedMessage
      id=""
      description="title for template picker for prep and send"
      defaultMessage="Select a template" />
  );
  // If buttons is an empty array, it shows the button background with no
  // buttons.
  let buttons: undefined | JSX.Element[];

  if (!isOpen) {
    // do nothing
  } else if (hasError) {
    content = (<span className={styles.error}>
      <FormattedMessage
        id=""
        description="error message when templates cannot be retrieved successfully"
        defaultMessage="Oops, something went wrong." />&nbsp;
      <a onClick={() => fetchTemplates(pageNum)}>
        <FormattedMessage
          id=""
          description="anchor text to retry retrieving templates"
          defaultMessage="Retry now" />
      </a>
    </span>);
  } else if (isFetching) {
    content = (
      <FormattedMessage
        id=""
        description="loading text when templates are being retrieved"
        defaultMessage="Loading page {pageNum, number}..."
        values={{ pageNum }} />
    );
  } else if (templates.length === 0 && totalResults === 0) {
    content = (
      <span className={styles.noTemplates}>
        <FormattedMessage
          id=""
          description="text block when there are no templates in the account"
          defaultMessage="Templates are great for standard documents like NDAs and employee contracts. Create a template once, then send it out as often as you need." />
      </span>
    );
    contentLabel = (
      <FormattedMessage
        id=""
        description="title for template picker modal when there are no templates"
        defaultMessage="No templates yet" />
    );
    buttons = [];
    buttons.push(
      <Button
        key="close"
        kind={ButtonType.Subtle}
        onClick={togglePicker(false)}
      >
        <FormattedMessage
          id=""
          description="Close button on a modal"
          defaultMessage="Close" />
      </Button>,
    );
    buttons.push(
      <Button key="create" onClick={() => { document.location.href = '/prep-and-send/new/4'; }} >
        <FormattedMessage
          id=""
          description="Modal button, links to create a template"
          defaultMessage="Create a template" />
      </Button>,
    );
  } else {
    const templateItems = templates
      .filter((template) => notHidden(template, templatesInRequest))
      .map((template, index) => (
        <Item
          key={template.id}
          index={index}
          template={template}
          onSelectTemplate={onSelectTemplate}
          templatesInRequest={templatesInRequest}
          isBulkSend={bulkSend} />
      ));

    const paginationDisplay = showModalFooter ? null : (
      <Pagination
        className={styles.pagination}
        setPage={fetchTemplates}
        page={pageNum}
        lastPage={lastPage} />
    );
    content = (
      <React.Fragment>
        <ul className={styles.templateList}>
          {templateItems}
        </ul>
        {paginationDisplay}
      </React.Fragment>
    );
  }
  const createTemplateUrl = HfReactHelper.urlHelper('prep-and-send/new/4');
  const modalFooter = showModalFooter ? (
    <div className={styles.footerContainer}>
      <div>
        <FormattedMessage
          id=""
          description="Upsell text saying to create a new template"
          defaultMessage="Can't find what you're looking for? <a>Create a new template</a>"
          values={{
            // eslint-disable-next-line react/display-name
            a: (str: string) => <a href={createTemplateUrl} target='_blank' rel='noopener noreferrer' onClick={handleCreateTemplate}>{str}</a>,
            numDocs: 3,
          }} />
      </div>
      <div className={styles.footer}>
        <Pagination
          className={styles.pagination}
          setPage={fetchTemplates}
          page={pageNum}
          lastPage={lastPage} />
      </div>
    </div>
  ) : null;

  return (
    <HelloModal
      buttons={buttons}
      contentLabel={contentLabel}
      width="540px"
      scrollable={false}
      isOpen={isOpen}
      onRequestClose={togglePicker(false)}
      extraClasses={styles.templatePickerModal}
      contentClasses={styles.templatePickerModal}
      footerOverride={modalFooter}
    >
      <div className={styles.templatePickerContainer}>
        {content}
      </div>
    </HelloModal>
  );
}
